import React, { useEffect, useState } from "react";
import { api, formatUAH, formatNum } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar,
} from "recharts";
import { ArrowUpRight, Activity, Banknote, TrendingUp, Wallet } from "lucide-react";
import PeriodFilter from "@/components/PeriodFilter";

function KPICard({ title, value, sub, icon: Icon, testid, tone = "navy" }) {
  const toneMap = {
    navy: "bg-[#0F2027] text-white",
    teal: "bg-[#0D9488] text-white",
    green: "bg-[#10B981] text-white",
    light: "bg-white text-[#0F2027] border border-slate-200",
  };
  return (
    <Card data-testid={testid} className="gv-card overflow-hidden">
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${toneMap[tone]}`}>
            <Icon className="h-5 w-5" />
          </div>
          <ArrowUpRight className="h-4 w-4 text-slate-300" />
        </div>
        <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">{title}</div>
        <div className="gv-kpi-value">{value}</div>
        {sub && <div className="text-xs text-slate-500 mt-2">{sub}</div>}
      </CardContent>
    </Card>
  );
}

function TopList({ title, items, testid, valueKey = "revenue", isCurrency = true }) {
  return (
    <Card data-testid={testid} className="gv-card">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="heading font-semibold text-[#0F2027]">{title}</h3>
          <span className="text-xs text-slate-400">{items.length}</span>
        </div>
        <ul className="space-y-3">
          {items.length === 0 && (
            <li className="text-sm text-slate-400">Немає даних</li>
          )}
          {items.map((it, idx) => (
            <li key={it.employee_id || it.iol_type || idx} className="flex items-center justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-7 w-7 rounded-full bg-teal-50 text-[#0D9488] text-xs font-semibold flex items-center justify-center">
                  {idx + 1}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-[#0F2027] truncate">
                    {it.full_name || it.iol_type}
                  </div>
                  {it.operations !== undefined && (
                    <div className="text-xs text-slate-500">{it.operations} операцій</div>
                  )}
                  {it.count !== undefined && (
                    <div className="text-xs text-slate-500">{it.count} шт.</div>
                  )}
                </div>
              </div>
              <div className="text-sm font-semibold text-[#0F2027] tabular-nums">
                {isCurrency ? formatUAH(it[valueKey]) : formatNum(it[valueKey])}
              </div>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [period, setPeriod] = useState({});

  useEffect(() => {
    if (!period.from && !period.to) return;
    const params = new URLSearchParams();
    if (period.from) params.set("from_date", period.from);
    if (period.to) params.set("to_date", period.to);
    api.get(`/dashboard/overview?${params}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Не вдалося завантажити дані"));
  }, [period]);

  if (err) return <div className="text-red-600">{err}</div>;

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="heading text-3xl font-bold text-[#0F2027]">Дашборд</h1>
          <p className="text-sm text-slate-500">Огляд ключових показників за обраний період</p>
        </div>
        <PeriodFilter value={period} onChange={setPeriod} />
      </div>

      {!data && <div data-testid="dashboard-loading" className="text-slate-500">Завантаження...</div>}
      {data && <DashboardContent data={data} />}
    </div>
  );
}

function DashboardContent({ data }) {
  const m = data.metrics;
  return (
    <>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard testid="kpi-ops-month" title="Операції" value={formatNum(m.operations_month)}
                 sub="Завершені операції" icon={Activity} tone="navy" />
        <KPICard testid="kpi-revenue-month" title="Виручка" value={formatUAH(m.revenue_month)}
                 sub="За обраний період" icon={Banknote} tone="teal" />
        <KPICard testid="kpi-profit-month" title="Прибуток" value={formatUAH(m.profit_month)}
                 sub="Виручка − собівартість" icon={TrendingUp} tone="green" />
        <KPICard testid="kpi-salary-fund" title={`Фонд ЗП (${m.salary_fund_percent}%)`}
                 value={formatUAH(m.salary_fund_month)} sub="За обраний період" icon={Wallet} tone="light" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <Card className="gv-card lg:col-span-2">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="heading font-semibold text-[#0F2027]">Виручка по днях</h3>
                <p className="text-xs text-slate-500">Завершені операції</p>
              </div>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.charts.revenue_by_day}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748B" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748B" }} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0" }} formatter={(v) => formatUAH(v)} />
                  <Line type="monotone" dataKey="revenue" stroke="#0D9488" strokeWidth={2.5} dot={{ r: 4, fill: "#0D9488" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="gv-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="heading font-semibold text-[#0F2027]">Операції по днях</h3>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.charts.revenue_by_day}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#64748B" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748B" }} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0" }} />
                  <Bar dataKey="operations" fill="#10B981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <TopList testid="top-surgeons" title="ТОП хірургів" items={data.top.surgeons} />
        <TopList testid="top-doctors" title="ТОП лікарів" items={data.top.doctors} />
        <TopList testid="top-managers" title="ТОП менеджерів" items={data.top.managers} />
        <TopList testid="top-salespeople" title="ТОП продавців" items={data.top.salespeople} />
      </div>

      <div className="mt-6">
        <TopList testid="top-iol" title="Найпопулярніші ІОЛ" items={data.top_iol}
                 valueKey="count" isCurrency={false} />
      </div>
    </>
  );
}
